// src/features/plan/KpiTileRow.jsx
//
// MARKET-PLAN-CROSSVIEW-CONSISTENCY-S1 — the single shared outer container
// for the Market Plan page's 5-tile KPI row. Plan/Timeline (KpiStrip in
// MarketPlanPage.jsx) and What-If (WhatIfKpiStrip in WhatIfPane.jsx) both
// render their tiles through this one component instead of each supplying
// its own copy of the grid/positioning CSS. That's what guarantees the row
// sits at the exact same height and screen position across all three views.
//
// KPI_TILE_ROW_HEIGHT is exported as a shared constant; callers may reference
// it for tile-floor sizing. The row itself now uses minHeight (not a hard
// height) so tiles can reflow to additional rows at narrow widths instead of
// overflowing the container.

import React, { useCallback, useContext, useEffect, useState } from "react";
import { UniversalSearchOpenContext } from "../../contexts/UniversalSearchContext.jsx";
import { KpiPopoverRowContext } from "../../components/KpiPopoverRowContext.jsx";

export const KPI_TILE_ROW_HEIGHT = 92;

export function KpiTileRow({ testId, children, columns = 5, condensed = false, contentId, collection = false, onExpand }) {
  const [activeId, setActiveId] = useState(null);
  const searchOpen = useContext(UniversalSearchOpenContext);

  const activate = useCallback((id) => {
    if (searchOpen) return;
    setActiveId((current) => current === id ? current : id);
  }, [searchOpen]);

  const leaveTile = useCallback((id) => {
    setActiveId((current) => current === id ? null : current);
  }, []);

  useEffect(() => {
    setActiveId(null);
  }, [searchOpen, condensed]);

  const contextValue = {
    activeId: searchOpen ? null : activeId,
    activate,
    leaveTile,
  };

  return (
    <KpiPopoverRowContext.Provider value={contextValue}>
      <div
        id={contentId}
        data-testid={testId}
        tabIndex={collection ? 0 : undefined}
        role={collection ? "group" : undefined}
        aria-label={collection ? "KPI metrics; use Arrow keys, Home or End to scroll horizontally" : undefined}
        onClick={condensed && onExpand ? (event) => {
          // Only the viewport's blank background, never a metric, its wrapper
          // or a portal event. Child explanations/actions keep their behavior.
          if (event.target === event.currentTarget) onExpand();
        } : undefined}
        onFocusCapture={collection ? (event) => {
          const viewport = event.currentTarget;
          if (event.target === viewport) return;
          const target = event.target.getBoundingClientRect();
          const bounds = viewport.getBoundingClientRect();
          if (target.right > bounds.right) viewport.scrollLeft += target.right - bounds.right;
          else if (target.left < bounds.left) viewport.scrollLeft -= bounds.left - target.left;
        } : undefined}
        onKeyDown={collection ? (event) => {
          const viewport = event.currentTarget;
          if (viewport.scrollWidth <= viewport.clientWidth) return;
          const step = Math.max(140, viewport.clientWidth * 0.75);
          const positions = {
            ArrowRight: viewport.scrollLeft + step,
            ArrowLeft: viewport.scrollLeft - step,
            Home: 0,
            End: viewport.scrollWidth,
          };
          if (positions[event.key] === undefined) return;
          event.preventDefault();
          viewport.scrollLeft = positions[event.key];
        } : undefined}
        style={{
          flex:                 "1 1 0",
          minWidth:             0,
          display:              condensed ? "flex" : "grid",
          ...(collection ? { overflowX: "auto", scrollbarWidth: "none" } : {}),
          ...(condensed ? { alignItems: "center", cursor: onExpand ? "pointer" : undefined } : {}),
          // Collection bands are one row even when the right control takes
          // a column's worth of space. Narrow screens scroll, never wrap.
          gridTemplateColumns: collection
            ? `repeat(${Math.max(1, React.Children.toArray(children).length)}, minmax(140px, 1fr))`
            : `repeat(auto-fit, minmax(140px, 1fr))`,
          gap:                  condensed ? 0 : 12,
          rowGap:               condensed ? 0 : 12,
          minHeight:            condensed ? undefined : KPI_TILE_ROW_HEIGHT,
          gridAutoRows:         "auto",
        }}
      >
        {children}
      </div>
    </KpiPopoverRowContext.Provider>
  );
}
